import React, { MouseEvent, ReactNode, useEffect } from 'react';

import classNames from 'classnames';
import { useSignMessage } from 'wagmi';

import { ExternalLink, Spinner } from '@uikit';

import { fetchTurtleClubSiweMessage } from '@api';

import { AngleRight } from '@uikit/icons/AngleRight';
import { ArrowRight } from '@uikit/icons/ArrowRight';
import { Checked } from '@uikit/icons/Checked';
import { Checked3 } from '@uikit/icons/Checked3';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useTurtleClubMembership, useVerifyTurtleClubSiwe } from '@shared/hooks/query/turtle-club';
import useCheckRequiredSelfData from '@shared/hooks/useCheckRequiredSelfData';
import useMagicIdUserWalletInfo from '@shared/hooks/useMagicIdUserWalletInfo';
import useSelfDataCheckLocalStorage from '@shared/hooks/useSelfDataCheckLocalStorage';

import { cn } from '@ui/uikit/utils';

export const JOIN_CAMPAIGN_STEP_TYPE = {
  REGISTER_MAGIC_STORE_ACCOUNT: 'REGISTER_MAGIC_STORE_ACCOUNT',
  ADD_EVM_WALLET: 'ADD_EVM_WALLET',
  USERNAME_AND_EMAIL: 'USERNAME_AND_EMAIL',
  LINK_TON_WALLET: 'LINK_TON_WALLET',
  VERIFY_HUMANITY: 'VERIFY_HUMANITY',
  VERIFY_HUMANITY_LIVENESS: 'VERIFY_HUMANITY_LIVENESS',
  VISIT_CAMPAIGN_PAGE: 'VISIT_CAMPAIGN_PAGE',
  MONITOR_LEADERBOARD: 'MONITOR_LEADERBOARD',
  STORE_TRADE_REWARD: 'STORE_TRADE_REWARD',
  LINK_TELEGRAM: 'LINK_TELEGRAM',
  JOIN_TURTLE_CLUB: 'JOIN_TURTLE_CLUB',
  START_EARNING_WITH_TURTLE_CLUB: 'START_EARNING_WITH_TURTLE_CLUB',
} as const;

type JoinCampaignStepName = keyof typeof JOIN_CAMPAIGN_STEP_TYPE;

export type JoinCampaignStepType = (typeof JOIN_CAMPAIGN_STEP_TYPE)[JoinCampaignStepName];

const genericStepsToJoinCampaign: {
  [key in JoinCampaignStepType | string]: StepsToJoinCampaignStep;
} = {
  [JOIN_CAMPAIGN_STEP_TYPE.REGISTER_MAGIC_STORE_ACCOUNT]: {
    id: 'register',
    title: 'Register for a Magic Store Account',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.ADD_EVM_WALLET]: {
    id: 'evm-wallet',
    title: 'Add an EVM Wallet to your MagicID',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.USERNAME_AND_EMAIL]: {
    id: 'username-and-email',
    title: 'Create a username and verify your email',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.LINK_TON_WALLET]: {
    id: 'ton-wallet',
    title: 'Link your TON Wallet to your MagicID',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY]: {
    id: 'complete-next-tasks',
    title: 'Complete one of the following',
    content: (
      <ul className="flex flex-col gap-2">
        {[
          'Pass Proof-of-Humanity verification',
          'Connect your Gitcoin Passport to your MagicID',
        ].map((item, i) => {
          return (
            <li key={i} className="flex items-center gap-3 max-md:text-sm">
              <Checked className="icon md:icon-[1.8125rem]" />
              {item}
            </li>
          );
        })}
      </ul>
    ),
  },
  [JOIN_CAMPAIGN_STEP_TYPE.VISIT_CAMPAIGN_PAGE]: {
    id: 'visit-campaign-page',
    title: 'Visit the Campaign Page to complete tasks',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.MONITOR_LEADERBOARD]: {
    id: 'leaderboard',
    title: (
      <>
        Monitor your standing on the Leaderboard to track your progress.
        <br />
        Rewards will be distributed based on your campaign XP proportionally
      </>
    ),
  },
  [JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY_LIVENESS]: {
    id: 'humanity-liveness',
    title: 'Complete Proof-of-Humanity verification',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.LINK_TELEGRAM]: {
    id: 'link-telegram',
    title: 'Link Telegram to your MagicID',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.JOIN_TURTLE_CLUB]: {
    id: 'join-turtle-club',
    title: 'Confirm Your Membership',
    content: 'Sign the message to confirm your free Turtle Club membership.',
    className: 'max-lg:flex-col max-lg:[&>div:last-child]:self-end',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.START_EARNING_WITH_TURTLE_CLUB]: {
    id: 'start-earning-with-turtle-club',
    title: 'Start Earning with Turtle Club',
    content: 'Begin exploring your earning opportunities today!',
    className: 'max-lg:flex-col max-lg:[&>div:last-child]:self-end',
  },
};

export const getStepsToJoinCampaign = (
  steps: JoinCampaignStepType[],
): StepsToJoinCampaignStep[] => {
  return steps.map((type) => {
    return {
      ...genericStepsToJoinCampaign[type],
      type,
    };
  });
};

const JoinCampaignButton = ({
  className,
  campaignUrl,
}: {
  className?: string;
  campaignUrl: string;
}) => {
  const { check, isAuth } = useCheckRequiredSelfData();

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (!check({ auth: true })) {
      event.preventDefault();

      return;
    }
  };

  return (
    <ExternalLink
      href={isAuth ? campaignUrl : '#'}
      onClick={handleClick}
      className={classNames(
        'button button-solid-secondary button-md md:button-xl md:min-h-12',
        className,
      )}
    >
      Join the Campaign Now
    </ExternalLink>
  );
};

export interface StepsToJoinCampaignStep {
  id: string;
  title: ReactNode;
  type?: JoinCampaignStepType;
  content?: ReactNode;
  className?: string;
}

const JoinTurtleClubActionButton = () => {
  const { check } = useCheckRequiredSelfData();

  const { isAuth } = useAuth();

  const { data: turtleClubMembership } = useTurtleClubMembership({ isAuth });

  const { getUserWalletAddress } = useMagicIdUserWalletInfo();

  const { signMessageAsync } = useSignMessage();

  const { mutateAsync: verifyTurtleClubSiwe } = useVerifyTurtleClubSiwe();

  const handleClick = async (event: MouseEvent<HTMLElement>) => {
    if (!check({ auth: true })) {
      event.preventDefault();

      return;
    }

    const { address } = getUserWalletAddress();

    const { signMessage, signupToken } = await fetchTurtleClubSiweMessage({
      address,
    });

    const signature = await signMessageAsync({
      message: signMessage,
    });

    await verifyTurtleClubSiwe({
      address,
      signature,
      signupToken,
    });
  };

  return (
    <ExternalLink
      onClick={handleClick}
      className={cn(
        'button button-solid-secondary button-md md:button-xl md:min-h-12 rounded-xl gap-2',
        {
          ['button-solid  button-color-green cursor-default pointer-events-none']:
            turtleClubMembership?.joined,
        },
      )}
    >
      {turtleClubMembership?.joined ? (
        'You have successfully signed up!'
      ) : (
        <>
          {'Join Turtle Club '} <AngleRight className="icon-lg" />
        </>
      )}
    </ExternalLink>
  );
};

const StartEarningWithTurtleClubActionButton = () => {
  const { check } = useCheckRequiredSelfData();

  const { isAuth } = useAuth();

  const { data: turtleClubMembership } = useTurtleClubMembership({ isAuth });

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (!check({ auth: true })) {
      event.preventDefault();

      return;
    }
  };

  return (
    <ExternalLink
      onClick={handleClick}
      href="https://app.turtle.club/?ref=MAGICSQR"
      className={cn(
        'button button-solid-secondary button-md md:button-xl md:min-h-12 rounded-xl gap-2',
        { ['button-disabled']: !turtleClubMembership?.joined },
      )}
    >
      Visit Turtle Club and Get Started <AngleRight className="icon-lg" />
    </ExternalLink>
  );
};

const StepActionButton = ({
  step,
  campaignUrl,
}: {
  step: StepsToJoinCampaignStep;
  campaignUrl?: string;
}) => {
  const { isLoading: isAuthLoading } = useAuth();
  const {
    check,
    hasEmail,
    hasEvmWallet,
    hasTonWallet,
    hasUsername,
    isAuth,
    hasFaceVerification,
    hasGitcoinVerification,
    hasLinkedTelegram,
    isHumanityVerificationLoading,
  } = useCheckRequiredSelfData();
  const { value: selfDataCheckLocalStorage } = useSelfDataCheckLocalStorage();

  const isAddEvmWalletStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.ADD_EVM_WALLET &&
    (hasEvmWallet || selfDataCheckLocalStorage.hasEvmWallet);
  const isLinkTonWalletStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.LINK_TON_WALLET &&
    (hasTonWallet || selfDataCheckLocalStorage.hasTonWallet);
  const isUsernameAndEmailStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.USERNAME_AND_EMAIL &&
    (hasEmail || selfDataCheckLocalStorage.hasEmail) &&
    (hasUsername || selfDataCheckLocalStorage.hasUsername);
  const isVerifyHumanityStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY &&
    (hasFaceVerification ||
      selfDataCheckLocalStorage.hasFaceVerification ||
      hasGitcoinVerification ||
      selfDataCheckLocalStorage.hasGitcoinVerification);
  const isRegisterMagicStoreAccountStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.REGISTER_MAGIC_STORE_ACCOUNT && isAuth;

  const isVerifyHUmanityLivenessStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY_LIVENESS &&
    (hasFaceVerification || selfDataCheckLocalStorage.hasFaceVerification);

  const isTelegramLinked =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.LINK_TELEGRAM &&
    (hasLinkedTelegram || selfDataCheckLocalStorage.hasLinkedTelegram);

  const isStepVerified =
    isAuth &&
    (isRegisterMagicStoreAccountStepVerified ||
      isAddEvmWalletStepVerified ||
      isLinkTonWalletStepVerified ||
      isUsernameAndEmailStepVerified ||
      isVerifyHumanityStepVerified ||
      isVerifyHUmanityLivenessStepVerified ||
      isTelegramLinked);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (!check({ auth: true })) {
      event.preventDefault();

      return;
    }
  };

  if (!step.type || step.type === JOIN_CAMPAIGN_STEP_TYPE.STORE_TRADE_REWARD) {
    return null;
  }

  if (
    step.type === JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY &&
    isHumanityVerificationLoading &&
    !isAuthLoading &&
    isAuth
  ) {
    return (
      <div className="h-8 w-8 md:h-12 md:w-12 flex justify-center items-center">
        <Spinner size="md" color="disabled" />
      </div>
    );
  }

  if (step.type === JOIN_CAMPAIGN_STEP_TYPE.VISIT_CAMPAIGN_PAGE) {
    return (
      <ExternalLink
        href={isAuth ? campaignUrl : '#'}
        onClick={handleClick}
        className="button button-solid-secondary button-md md:button-xl md:min-h-12 rounded-xl gap-2"
      >
        Join Now <ArrowRight className="icon-xs" />
      </ExternalLink>
    );
  }

  if (step.type === JOIN_CAMPAIGN_STEP_TYPE.MONITOR_LEADERBOARD) {
    return (
      <a
        onClick={handleClick}
        href="#leaderboard"
        className="icon-button button-text-primary rotate-90"
      >
        <AngleRight className="icon-lg" />
      </a>
    );
  }

  if (step.type === JOIN_CAMPAIGN_STEP_TYPE.JOIN_TURTLE_CLUB) {
    return <JoinTurtleClubActionButton />;
  }

  if (step.type === JOIN_CAMPAIGN_STEP_TYPE.START_EARNING_WITH_TURTLE_CLUB) {
    return <StartEarningWithTurtleClubActionButton />;
  }

  if (isStepVerified) {
    return (
      <div className="h-8 w-8 md:h-12 md:w-12 flex justify-center items-center">
        <Checked3 className="icon-lg text-green-700" />
      </div>
    );
  }

  // TODO: rework to make actions on the current page
  const url = {
    [JOIN_CAMPAIGN_STEP_TYPE.REGISTER_MAGIC_STORE_ACCOUNT]: '/store',
    [JOIN_CAMPAIGN_STEP_TYPE.ADD_EVM_WALLET]: '/store/profile/settings',
    [JOIN_CAMPAIGN_STEP_TYPE.USERNAME_AND_EMAIL]: '/store/profile/settings?fillProfile=true',
    [JOIN_CAMPAIGN_STEP_TYPE.LINK_TON_WALLET]: '/store/profile/settings',
    [JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY]: '/store/profile/settings',
    [JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY_LIVENESS]: '/store/profile/settings',
    [JOIN_CAMPAIGN_STEP_TYPE.LINK_TELEGRAM]: '/store/profile/settings',
  }[step.type];

  return (
    <a
      href={isAuth ? url : '#'}
      onClick={handleClick}
      target="_blank"
      rel="noreferrer"
      className="button button-solid-secondary button-md md:button-xl md:min-h-12 rounded-xl gap-2"
    >
      Verify <ArrowRight className="icon-xs" />
    </a>
  );
};

interface StepsToJoinCampaignProps {
  steps: StepsToJoinCampaignStep[];
  campaignUrl?: string;
  className?: string;
}

const StepsToJoinCampaign = ({ campaignUrl, steps, className }: StepsToJoinCampaignProps) => {
  const showJoinCampaignButton = !!campaignUrl;
  const { resetValue: resetSelfDataCheckLocalStorage } = useSelfDataCheckLocalStorage();

  useEffect(() => {
    // NOTE: reset local storage to initial state when page is opened
    // to make it consistent across tabs
    resetSelfDataCheckLocalStorage();
  }, [resetSelfDataCheckLocalStorage]);

  return (
    <div className={classNames('flex flex-col', className)}>
      <div className="p-4 md:p-6 rounded-2xl bg-neutral-50 mb-5 md:mb-10">
        <ul className="flex flex-col gap-4 md:gap-6">
          {steps.map((step, i) => {
            const showActionButton = !!step.type;

            return (
              <li
                key={step.id}
                className={cn(
                  'flex max-md:flex-col md:items-start p-4 md:p-6 gap-5 md:gap-3 border border-primary-200 rounded-lg',
                  step.className,
                )}
              >
                <div className="flex gap-3 flex-1">
                  <span className="rounded-full self-start flex-shrink-0 flex justify-center text-lg md:text-2xl font-semibold items-center w-7 md:w-8 bg-gradient-to-r text-neutral-50 from-[#FF6647] via-[#E14BFF] to-[#4AA5FF]">
                    {i + 1}
                  </span>

                  <div className="flex flex-col gap-2.5 w-full">
                    <p className="text-lg md:text-2xl font-semibold">{step.title}</p>

                    {step.content}
                  </div>
                </div>

                {showActionButton && (
                  <div className="max-md:self-end">
                    <StepActionButton campaignUrl={campaignUrl} step={step} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <p className="max-md:text-xs text-right text-neutral-500 mt-2.5">
          Only one account per user is permitted. Duplicate accounts will be blocked from the
          campaign.
        </p>
      </div>

      {showJoinCampaignButton && (
        <JoinCampaignButton campaignUrl={campaignUrl} className="mx-auto" />
      )}
    </div>
  );
};

export default StepsToJoinCampaign;
